import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/* Hover intent for the desktop mega menu.
 * An open panel waits CLOSE_DELAY before closing, so the pointer can travel to the panel.
 * Another top-level link only takes over once the pointer comes to rest on it (moves less than
 * INTENT_SENSITIVITY px in INTENT_INTERVAL ms), so sweeping across neighbouring links on the
 * way to the panel, at any speed, does not steal it. */
const CLOSE_DELAY = 400;
const INTENT_INTERVAL = 100;
const INTENT_SENSITIVITY = 6;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointermove', this.#onPointerMove);
    document.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('keydown', this.#onKeyDown);
    this.#cancelClose();
    this.#cancelSwitch();
    this.#cleanupMutationObserver();
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #switchTimer;

  /** @type {HTMLElement | null} */
  #switchTarget = null;

  /** Last known pointer position. */
  #pointer = { x: 0, y: 0 };

  /**
   * Top-level items in the visible row use hover intent. Overflow ("More") items keep the stock behaviour.
   * @param {EventTarget | null} element
   * @returns {element is HTMLElement}
   */
  #isIntentItem(element) {
    return element instanceof HTMLElement && element.matches('.menu-list__list-item') && element.slot === '';
  }

  /** @returns {HTMLElement | null} */
  get #activeListItem() {
    return /** @type {HTMLElement | null} */ (this.#state.activeItem?.closest('.menu-list__list-item') ?? null);
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  #cancelSwitch() {
    clearTimeout(this.#switchTimer);
    this.#switchTimer = undefined;
    this.#switchTarget = null;
  }

  #scheduleClose() {
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      if (this.#activeListItem?.matches(':hover')) return;
      // Still somewhere over the menu row or panel (e.g. the strip under a neighbouring link): check again later.
      if (this.matches(':hover')) {
        this.#scheduleClose();
        return;
      }
      this.#deactivate();
    }, CLOSE_DELAY);
  }

  /**
   * Decide what the pointer means as it moves over the menu.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (event.pointerType === 'touch' || !(event.target instanceof Element)) return;

    this.#pointer.x = event.clientX;
    this.#pointer.y = event.clientY;

    const listItem = event.target.closest('.menu-list__list-item');
    if (!this.#isIntentItem(listItem)) return;

    // Inside the open item (its link, the bridge under it, or its panel): keep it open.
    if (listItem === this.#activeListItem) {
      this.#cancelClose();
      this.#cancelSwitch();
      return;
    }

    // Only the link itself can take over. The strip under a neighbouring link is just a path to the panel.
    if (!event.target.closest('.menu-list__link')) {
      this.#cancelSwitch();
      return;
    }

    if (!this.#state.activeItem) {
      this.#cancelClose();
      this.#open(listItem);
      return;
    }

    if (this.#switchTarget === listItem) return;

    this.#cancelSwitch();
    this.#switchTarget = listItem;

    let last = { ...this.#pointer };
    const checkIntent = () => {
      const target = this.#switchTarget;
      if (!target?.querySelector('.menu-list__link')?.matches(':hover')) {
        this.#cancelSwitch();
        return;
      }

      // Resting on the link's text row counts. Its lower edge is the path down to the open panel.
      const title = target.querySelector('.menu-list__link-title')?.getBoundingClientRect();
      const onTitleRow = !title || (this.#pointer.y >= title.top - 8 && this.#pointer.y <= title.bottom + 8);

      const moved = Math.hypot(this.#pointer.x - last.x, this.#pointer.y - last.y);
      if (moved < INTENT_SENSITIVITY && onTitleRow) {
        this.#cancelSwitch();
        this.#cancelClose();
        this.#open(target);
        return;
      }

      last = { ...this.#pointer };
      this.#switchTimer = setTimeout(checkIntent, INTENT_INTERVAL);
    };
    this.#switchTimer = setTimeout(checkIntent, INTENT_INTERVAL);
  };

  /**
   * Escape closes the open panel.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;
    const item = this.#state.activeItem;
    this.#cancelClose();
    this.#cancelSwitch();
    this.#deactivate();
    if (this.contains(document.activeElement)) item.focus();
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    // Pointer hover on the visible row is handled by #onPointerMove.
    if (event.type === 'pointerenter' && this.#isIntentItem(event.target)) {
      if (/** @type {PointerEvent} */ (event).pointerType !== 'touch') {
        this.#onPointerMove(/** @type {PointerEvent} */ (event));
        return;
      }
    }

    this.#cancelClose();
    this.#cancelSwitch();
    this.#open(event.target);
  };

  /**
   * Open the submenu for a list item immediately.
   * @param {Element} target - the list item
   */
  #open(target) {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!this.headerComponent) return;

    let item = findMenuItem(target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = /** @type {HTMLElement} */ (target).slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Leaving a visible-row item by pointer: wait before closing, so the pointer can reach the panel.
    if (event.type === 'pointerleave' && this.#isIntentItem(event.target)) {
      if (this.#switchTarget === event.target) this.#cancelSwitch();
      if (event.target === this.#activeListItem) this.#scheduleClose();
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
